import type {
  StoreContactDetailsFragment,
  StoreLowestDeliveryPricesFragment,
} from '@shared/gql/document-nodes'
import type { FC } from 'react'

import {
  CommentOutline24,
  InfoOutline24,
  LinkOutline16,
  LocationOutline24,
  PhoneOutline24,
} from '@sok/design-system'
import { NextLink } from 'components/NextLink'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { parsePhoneNumber } from 'react-phone-number-input'
import styled from 'styled-components'

import {
  StoreInfoCard,
  StoreInfoCardItem,
  StoreInfoCardItemDescription,
  StoreInfoCardItemExpandable,
  StoreInfoCardItemWrapper,
  StoreInfoCardTitle,
} from './StoreInfoCard'

const StyledStoreInfoCard = styled(StoreInfoCard).attrs({ as: 'address' })({
  fontStyle: 'normal',
})

const StyledLinkOutline16 = styled(({ className }) => <LinkOutline16 className={className} />)(
  ({ theme }) => ({
    position: 'relative',
    top: '2px',
    marginLeft: theme.spacings.xxSmall,
  }),
)

const TooltipContainer = styled.p(({ theme }) => ({
  padding: `${theme.spacings.xxSmall} ${theme.spacings.large}`,
  backgroundColor: theme.colors.grey100,
}))

const getLocationLink = (storeName: string, storeAddress: string) => {
  const searchQuery = `${storeName}, ${storeAddress}`

  return `https://maps.google.com/maps?z=10&t=m&q=${encodeURIComponent(searchQuery)}`
}

export const StoreContactInfo: FC<{
  store: StoreContactDetailsFragment & {
    name: string
  }
  deliveryAreas: StoreLowestDeliveryPricesFragment
}> = ({ store, deliveryAreas }) => {
  const { t } = useTranslation()

  const address = store.location?.address
  const phoneNumber = store.contactInfo?.phoneNumber
  const feedbackUrl = store.feedback?.url

  const parsedNumber = useMemo(() => {
    return parsePhoneNumber(phoneNumber?.number ?? '', 'FI')
  }, [phoneNumber?.number])

  if (!address && !phoneNumber && !feedbackUrl) {
    return null
  }

  const storeAddress = address
    ? `${address.street.default}, ${address.postcode} ${address.postcodeName?.default}`
    : ''

  const locationLink = getLocationLink(store.name, storeAddress)

  const pickupInfo = deliveryAreas.lowestDeliveryPrices?.find(
    (slot) => slot?.deliveryMethod === 'PICKUP',
  )
  const fastTrackPickupInfo = deliveryAreas.lowestDeliveryPrices?.find(
    (slot) => slot?.deliveryMethod === 'PICKUP' && slot.isFastTrack,
  )

  return (
    <StyledStoreInfoCard>
      <StoreInfoCardTitle>{t('Store contact info_StorePage')}</StoreInfoCardTitle>
      <StoreInfoCardItemDescription>
        <span>{t('Are you looking for a store address or do you want to contact the store?')}</span>
      </StoreInfoCardItemDescription>
      {address && (
        <>
          <NextLink href={locationLink} external>
            <StoreInfoCardItemWrapper>
              <StoreInfoCardItem>
                <LocationOutline24 />
                <span>
                  {storeAddress} <StyledLinkOutline16 />
                </span>
              </StoreInfoCardItem>
            </StoreInfoCardItemWrapper>
          </NextLink>

          {pickupInfo?.description && (
            <StoreInfoCardItemExpandable
              icon={<InfoOutline24 />}
              title={t('Pickup location available')}
            >
              <p>{pickupInfo.description}</p>

              {fastTrackPickupInfo && (
                <TooltipContainer>{t('Fast track pickup location info')}</TooltipContainer>
              )}
            </StoreInfoCardItemExpandable>
          )}
        </>
      )}

      {phoneNumber && (
        <NextLink href={`tel:${parsedNumber?.number}`}>
          <StoreInfoCardItemWrapper>
            <StoreInfoCardItem>
              <PhoneOutline24 />
              <span>
                <span>{parsedNumber?.formatInternational()}</span>
                {phoneNumber.callCharge && (
                  <>
                    {' '}
                    <small>{`(${phoneNumber.callCharge.default})`}</small>
                  </>
                )}
              </span>
            </StoreInfoCardItem>
          </StoreInfoCardItemWrapper>
        </NextLink>
      )}

      {feedbackUrl && (
        <NextLink href={feedbackUrl.default} external>
          <StoreInfoCardItemWrapper>
            <StoreInfoCardItem>
              <CommentOutline24 />
              <span>
                {t('Give feedback to store')} <StyledLinkOutline16 />
              </span>
            </StoreInfoCardItem>
          </StoreInfoCardItemWrapper>
        </NextLink>
      )}
    </StyledStoreInfoCard>
  )
}
